/**
 * Before photos and After photos, room by room, driven by the owner's spots.
 * Room start (swipe the reference photos) → whole-room shot → one shot per spot → "out in the open" card → room done.
 * Every photo is Keep or Retake before it saves. Problem is chips only. No typing anywhere.
 * After photos use the same list; the cleaner's own Before photo sits in the corner of the camera ("Your before").
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Camera, Check, ChevronRight, Loader2, AlertTriangle, RotateCcw } from "lucide-react";
import { cn } from "@/lib/utils";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";
import { useTranslation } from "@/hooks/useTranslation";
import { useCreateMaintenanceReport } from "@/hooks/useMaintenanceReports";
import {
  appendPhotoCaptureRecord,
  captureFrameFromVideo,
  uploadInAppCleaningPhoto,
} from "@/lib/cleaningPhotoCapture";
import {
  readSimpleFlowState,
  type GuidedShot,
  type InspectFinding,
  type RoomShots,
  type RoomSkipReason,
  type SimpleFlowChromeOpts,
  type SimpleFlowState,
} from "@/lib/simpleTurnover";
import { simpleFlowSegmentIndex } from "@/lib/turnoverWizardPlan";
import { flattenRoomPhotos, readByRoom, roomStatus, type RoomPhase, type WalkRoom } from "@/lib/roomPhotos";
import { SimpleStepHeader } from "@/components/cleaner/SimpleStepHeader";
import { OfflineLine } from "@/components/cleaner/OfflineLine";
import {
  CollageThumb,
  CornerThumb,
  ItemChips,
  ItemCollage,
  PhotoOverlay,
  ProblemPill,
  RefCarousel,
  Segments,
} from "@/components/cleaner/BeforeAfterParts";
import { CLEANER_SIMPLE_CTA_FOOTER, CLEANER_SIMPLE_STEP_SHELL } from "@/lib/turnoverChrome";

type Screen = "guide" | "camera" | "preview" | "open" | "complete" | "finish";
type CamMode =
  | { kind: "shot"; key: string }
  | { kind: "retake"; key: string }
  | { kind: "extra" }
  | { kind: "problem" };

type Preview = {
  mode: Exclude<CamMode, { kind: "problem" }>;
  blob: Blob;
  localUrl: string;
  width: number;
  height: number;
};

interface Props {
  rooms: WalkRoom[];
  phase?: RoomPhase;
  propertyId: string;
  taskId: string;
  cleanerId?: string | null;
  checklistName?: string | null;
  entryWalkthroughState: unknown;
  includeMaintenance?: boolean;
  includeQuickCheck?: boolean;
  includeGuestSetup?: boolean;
  onComplete: () => void;
  onBack?: () => void;
  onSelectSegment?: (index: number) => void;
}

const SKIP_REASONS: { id: RoomSkipReason; key: string }[] = [
  { id: "locked", key: "rwLocked" },
  { id: "guest", key: "rwGuest" },
  { id: "unsafe", key: "rwUnsafe" },
];

const CATEGORIES = [
  { id: "missing", key: "bpMissing" },
  { id: "broken", key: "bpBroken" },
  { id: "dirty", key: "bpDirty" },
] as const;
type Category = (typeof CATEGORIES)[number]["id"];

function fill(text: string, vars: Record<string, string | number>): string {
  return Object.entries(vars).reduce((acc, [k, v]) => acc.split(`{{${k}}}`).join(String(v)), text);
}

/** photo_urls = guided shots in list order, then extras, so every older reader of the room keeps working. */
function rebuildUrls(room: WalkRoom, shots: Record<string, GuidedShot>, extras: { url: string }[]): string[] {
  const out: string[] = [];
  for (const s of room.shots) {
    const url = shots[s.key]?.url;
    if (url) out.push(url);
  }
  for (const e of extras) if (e.url) out.push(e.url);
  return out;
}

export function GuidedWalkStep({
  rooms,
  phase = "before",
  propertyId,
  taskId,
  cleanerId,
  checklistName,
  entryWalkthroughState,
  includeMaintenance = false,
  includeQuickCheck = false,
  includeGuestSetup = false,
  onComplete,
  onBack,
  onSelectSegment,
}: Props) {
  const { t, tDynamic } = useTranslation();
  const isAfter = phase === "after";
  const chrome: SimpleFlowChromeOpts = { includeMaintenance, includeQuickCheck, includeGuestSetup };
  const createReport = useCreateMaintenanceReport();

  const flowRef = useRef<SimpleFlowState>(readSimpleFlowState(entryWalkthroughState));
  // Before's answers do not change while After is open: its photos are "Your before", its skips carry over.
  const beforeBy = useMemo(() => flowRef.current.walk_by_room ?? {}, []);
  const [by, setBy] = useState<Record<string, RoomShots>>(() => readByRoom(flowRef.current, phase));

  const shotsOf = useCallback(
    (roomId: string): RoomShots | undefined => {
      if (isAfter && beforeBy[roomId]?.skip) return { photo_urls: [], skip: beforeBy[roomId].skip };
      return by[roomId];
    },
    [by, isAfter, beforeBy],
  );
  const statusOf = useCallback((roomId: string) => roomStatus(shotsOf(roomId)), [shotsOf]);

  const initialIndex = useMemo(() => {
    const idx = rooms.findIndex((r) => statusOf(r.id) === "open");
    return idx < 0 ? 0 : idx;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const [roomIndex, setRoomIndex] = useState(initialIndex);
  const [screen, setScreen] = useState<Screen>(() =>
    rooms.every((r) => statusOf(r.id) !== "open") ? "finish" : "guide",
  );
  const [cam, setCam] = useState<CamMode | null>(null);
  const [preview, setPreview] = useState<Preview | null>(null);
  const [swap, setSwap] = useState(false);
  const [enlarge, setEnlarge] = useState<{ url: string; label: string } | null>(null);
  const [busy, setBusy] = useState(false);
  const [flash, setFlash] = useState(false);
  const [cameraReady, setCameraReady] = useState(false);
  const [cameraError, setCameraError] = useState<string | null>(null);
  const [skipOpen, setSkipOpen] = useState(false);
  const [skipReason, setSkipReason] = useState<RoomSkipReason | null>(null);
  const [openAck, setOpenAck] = useState<Record<string, boolean>>({});
  const [showAllNames, setShowAllNames] = useState(false);

  // Problem: chips only (item, what happened), optional photo.
  const [issueOpen, setIssueOpen] = useState(false);
  const [issueCat, setIssueCat] = useState<Category | null>(null);
  const [issuePhotoUrl, setIssuePhotoUrl] = useState<string | null>(null);
  const [issueItemId, setIssueItemId] = useState<string | null | undefined>(undefined);
  const [issueSent, setIssueSent] = useState<string | null>(null);

  const videoRef = useRef<HTMLVideoElement>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const shutterLock = useRef(false);

  const room = rooms[Math.min(roomIndex, rooms.length - 1)];
  const roomShots = by[room.id];
  const taken = roomShots?.shots ?? {};
  const extras = roomShots?.extras ?? [];
  const keys = room.shots.map((s) => s.key);
  const takenCount = keys.filter((k) => taken[k]).length;
  const cursorKey = keys.find((k) => !taken[k]) ?? null;
  const roomComplete = cursorKey === null;
  const shot = room.shots.find((s) => s.key === cursorKey) ?? room.shots[0];
  const shotIdx = keys.indexOf(shot.key);
  const spotCount = room.shots.length - 1;
  const nextRoom = rooms.slice(roomIndex + 1).find((r) => statusOf(r.id) === "open") ?? null;
  const doneRooms = rooms.filter((r) => statusOf(r.id) === "done").length;

  /** The cleaner's own Before photo for a shot (After only). */
  const beforeUrlFor = (key: string): string | null => {
    const b = beforeBy[room.id];
    return b?.shots?.[key]?.url ?? (key === "room" ? b?.photo_urls?.[0] ?? null : null);
  };

  const save = useCallback(
    async (nextBy: Record<string, RoomShots>, extra?: Partial<SimpleFlowState>) => {
      setBy(nextBy);
      const flat = flattenRoomPhotos(rooms, nextBy);
      flowRef.current = {
        ...flowRef.current,
        simple_flow: true,
        ...(isAfter ? { after_by_room: nextBy, after_photo_urls: flat } : { walk_by_room: nextBy, before_photo_urls: flat }),
        ...extra,
      };
      const { error } = await supabase
        .from("cleaning_tasks")
        .update({ entry_walkthrough_state: flowRef.current as never })
        .eq("id", taskId);
      if (error) throw error;
    },
    [isAfter, rooms, taskId],
  );

  const stopCamera = useCallback(() => {
    streamRef.current?.getTracks().forEach((tr) => tr.stop());
    streamRef.current = null;
    if (videoRef.current) videoRef.current.srcObject = null;
    setCameraReady(false);
  }, []);

  const startCamera = useCallback(async () => {
    setCameraError(null);
    if (!navigator.mediaDevices?.getUserMedia) {
      setCameraError(t("cameraUnavailableOpen"));
      return;
    }
    try {
      stopCamera();
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: false,
        video: { facingMode: { ideal: "environment" }, width: { ideal: 1920 }, height: { ideal: 1080 } },
      });
      streamRef.current = stream;
      const video = videoRef.current;
      if (video) {
        video.srcObject = stream;
        video.setAttribute("playsinline", "true");
        video.muted = true;
        await video.play().catch(() => undefined);
      }
      setCameraReady(true);
    } catch (err) {
      const name = err instanceof Error ? err.name : "";
      setCameraError(name === "NotAllowedError" ? t("allowCameraKeepShooting") : t("couldNotOpenCameraTap"));
      setCameraReady(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stopCamera]);

  useEffect(() => {
    if (screen === "camera") void startCamera();
    else stopCamera();
    return () => stopCamera();
  }, [screen, startCamera, stopCamera]);

  const openCamera = (mode: CamMode) => {
    if (shutterLock.current) return;
    setCam(mode);
    setScreen("camera");
  };

  const closeCamera = () => {
    setCam(null);
    setScreen("guide");
  };

  const storagePathFor = () =>
    `${propertyId}/${taskId}/simple-${isAfter ? "after" : "before"}/room-${room.id}/${crypto.randomUUID()}.jpg`;

  /** Take the picture; shots go to Keep or Retake first, a problem photo goes straight to its sheet. */
  const shutter = async () => {
    if (shutterLock.current || busy || !cam) return;
    const video = videoRef.current;
    if (!video || !cameraReady) {
      toast({ title: t("cameraRequired"), description: t("cameraRequiredDesc"), variant: "destructive" });
      void startCamera();
      return;
    }
    shutterLock.current = true;
    setBusy(true);
    try {
      const frame = await captureFrameFromVideo(video);
      setFlash(true);
      window.setTimeout(() => setFlash(false), 180);
      if (cam.kind === "problem") {
        const { url, record } = await uploadInAppCleaningPhoto({
          propertyId,
          taskId,
          moment: "issue",
          blob: frame.blob,
          width: frame.width,
          height: frame.height,
          storagePath: storagePathFor(),
        });
        flowRef.current = {
          ...flowRef.current,
          photo_captures: appendPhotoCaptureRecord(flowRef.current.photo_captures, record),
        };
        setIssuePhotoUrl(url);
        setCam(null);
        setScreen("guide");
        setIssueOpen(true);
        return;
      }
      setSwap(false);
      setPreview({ mode: cam, blob: frame.blob, localUrl: URL.createObjectURL(frame.blob), width: frame.width, height: frame.height });
      setCam(null);
      setScreen("preview");
    } catch (err) {
      toast({
        title: t("captureFailed"),
        description: err instanceof Error ? err.message : t("tryAgain"),
        variant: "destructive",
      });
    } finally {
      setBusy(false);
      shutterLock.current = false;
    }
  };

  const dropPreview = () => {
    if (preview?.localUrl.startsWith("blob:")) URL.revokeObjectURL(preview.localUrl);
    setPreview(null);
  };

  /** Keep: upload and save the photo, then move on. */
  const keep = async (thenAnother = false) => {
    if (!preview || busy) return;
    setBusy(true);
    try {
      const { url, record } = await uploadInAppCleaningPhoto({
        propertyId,
        taskId,
        moment: isAfter ? "after" : "walk",
        blob: preview.blob,
        width: preview.width,
        height: preview.height,
        storagePath: storagePathFor(),
      });
      const captures = appendPhotoCaptureRecord(flowRef.current.photo_captures, record);
      const now = new Date().toISOString();
      const mode = preview.mode;
      const nextShots = { ...taken };
      let nextExtras = extras;
      if (mode.kind === "shot") nextShots[mode.key] = { url, at: now };
      else if (mode.kind === "retake") nextShots[mode.key] = { url, at: taken[mode.key]?.at ?? now, retaken_at: now };
      else nextExtras = [...extras, { url, at: now }];
      const nextRoom: RoomShots = {
        ...roomShots,
        shots: nextShots,
        extras: nextExtras,
        photo_urls: rebuildUrls(room, nextShots, nextExtras),
      };
      await save({ ...by, [room.id]: nextRoom }, { photo_captures: captures });
      dropPreview();
      if (thenAnother) {
        openCamera({ kind: "extra" });
      } else {
        setScreen("guide");
      }
    } catch (err) {
      toast({
        title: t("captureFailed"),
        description: err instanceof Error ? err.message : t("tryAgain"),
        variant: "destructive",
      });
    } finally {
      setBusy(false);
    }
  };

  const goNextRoom = async () => {
    setBusy(true);
    try {
      const next = { ...by, [room.id]: { ...by[room.id], photo_urls: by[room.id]?.photo_urls ?? [], done: true, skip: undefined } };
      await save(next);
      const following = rooms.findIndex((r, i) => i > roomIndex && roomStatusOf(next[r.id], r.id) === "open");
      if (following >= 0) {
        setRoomIndex(following);
        setScreen("guide");
      } else {
        setScreen("finish");
      }
    } catch (err) {
      toast({ title: t("couldNotContinue"), description: err instanceof Error ? err.message : t("tryAgain"), variant: "destructive" });
    } finally {
      setBusy(false);
    }
  };

  /** Status for a room using a freshly saved map (After carries Before's skips). */
  const roomStatusOf = (s: RoomShots | undefined, roomId: string) =>
    isAfter && beforeBy[roomId]?.skip ? "skipped" : roomStatus(s);

  const skipThisRoom = async () => {
    if (!skipReason) return;
    setBusy(true);
    try {
      const next = {
        ...by,
        [room.id]: {
          photo_urls: by[room.id]?.photo_urls ?? [],
          shots: by[room.id]?.shots,
          extras: by[room.id]?.extras,
          skip: skipReason,
          skipped_at: new Date().toISOString(),
        },
      };
      await save(next);
      setSkipOpen(false);
      setSkipReason(null);
      const following = rooms.findIndex((r, i) => i > roomIndex && roomStatusOf(next[r.id], r.id) === "open");
      if (following >= 0) {
        setRoomIndex(following);
        setScreen("guide");
      } else {
        setScreen("finish");
      }
    } catch (err) {
      toast({ title: t("couldNotSave"), description: err instanceof Error ? err.message : undefined, variant: "destructive" });
    } finally {
      setBusy(false);
    }
  };

  const shootSkippedNow = async (roomId: string) => {
    const prev = by[roomId];
    const next = { ...by, [roomId]: { photo_urls: prev?.photo_urls ?? [], shots: prev?.shots, extras: prev?.extras } };
    try {
      await save(next);
    } catch {
      /* local state still moves on */
    }
    setRoomIndex(Math.max(0, rooms.findIndex((r) => r.id === roomId)));
    setScreen("guide");
  };

  const openRoom = (roomId: string) => {
    setOpenAck((p) => ({ ...p, [roomId]: true }));
    setRoomIndex(Math.max(0, rooms.findIndex((r) => r.id === roomId)));
    setScreen("guide");
  };

  const finish = async () => {
    setBusy(true);
    try {
      const flat = flattenRoomPhotos(rooms, by);
      if (isAfter) {
        await save(by, { after_photo_urls: flat });
        await supabase.from("cleaning_tasks").update({ checklist_completed: true }).eq("id", taskId);
      } else {
        await save(by, { before_photo_urls: flat, walk_complete: true, before_complete: true, inspect_complete: true });
      }
      stopCamera();
      onComplete();
    } catch (err) {
      toast({ title: t("couldNotContinue"), description: err instanceof Error ? err.message : t("tryAgain"), variant: "destructive" });
    } finally {
      setBusy(false);
    }
  };

  // ---------- problem ----------
  const itemsForShot = useMemo(() => {
    const groupIds = new Set(room.shots.filter((s) => s.kind === "group").map((s) => s.key));
    const list =
      shot.kind === "group"
        ? room.items.filter((i) => i.groupId === shot.key)
        : room.items.filter((i) => !i.groupId || !groupIds.has(i.groupId));
    return list.length > 0 ? list : room.items;
  }, [room, shot]);

  const openProblem = () => {
    setIssueCat(null);
    setIssuePhotoUrl(null);
    setIssueItemId(undefined);
    setIssueSent(null);
    setIssueOpen(true);
  };

  const sendIssue = async () => {
    if (issueItemId === undefined || !issueCat) return;
    setBusy(true);
    try {
      const item = room.items.find((i) => i.id === issueItemId) ?? null;
      const catWord = t(CATEGORIES.find((c) => c.id === issueCat)!.key);
      const what = item ? item.name : room.name;
      const finding: InspectFinding = {
        status: issueCat === "missing" ? "missing" : "broken",
        note: issueCat === "dirty" ? "Dirty" : null,
        evidence_photo_url: issuePhotoUrl,
        reference_photo_url: item?.photoUrl ?? null,
        reference_photo_id: item?.id ?? null,
        room_name: room.name,
      };
      await save(by, { inspect_findings: [...(flowRef.current.inspect_findings ?? []), finding] });
      if (cleanerId) {
        try {
          await createReport.mutateAsync({
            property_id: propertyId,
            cleaning_task_id: taskId,
            cleaner_id: cleanerId,
            checklist_name: checklistName || undefined,
            room_name: room.name,
            response: issueCat === "broken" ? "needs_repair" : "no",
            notes: `${issueCat.toUpperCase()}: ${what}`,
            photos: issuePhotoUrl ? [issuePhotoUrl] : [],
            task_description: `${issueCat === "missing" ? "Missing" : issueCat === "broken" ? "Broken" : "Dirty"}: ${what}`,
            unit_item_id: item?.kind === "unit" ? item.id : null,
          });
        } catch {
          /* the finding is already saved on the job */
        }
      }
      setIssueSent(`${what} · ${catWord}`);
    } catch (err) {
      toast({ title: t("couldNotSave"), description: err instanceof Error ? err.message : undefined, variant: "destructive" });
    } finally {
      setBusy(false);
    }
  };

  const problemsInRoom = (name: string) =>
    (flowRef.current.inspect_findings ?? []).filter(
      (f) => f.room_name === name && (f.status === "missing" || f.status === "broken"),
    ).length;
  const problemsTotal = (flowRef.current.inspect_findings ?? []).filter((f) => f.status === "missing" || f.status === "broken").length;

  const header = (
    <SimpleStepHeader
      title={t(isAfter ? "afterTitle" : "walkTitle")}
      activeSegment={simpleFlowSegmentIndex(isAfter ? "after_photos" : "walk", chrome)}
      includeMaintenance={includeMaintenance}
      includeQuickCheck={includeQuickCheck}
      includeGuestSetup={includeGuestSetup}
      onBack={onBack}
      onSelectSegment={onSelectSegment}
    />
  );

  const cta = (children: React.ReactNode) => (
    <div className={cn("flex shrink-0 flex-col gap-2.5 border-t border-[#e6edf1] bg-white", CLEANER_SIMPLE_CTA_FOOTER)}>{children}</div>
  );
  const bigButton = "flex h-[64px] w-full items-center justify-center gap-3 rounded-[18px] bg-[#15776d] text-[20px] font-bold text-white disabled:opacity-60";
  const roomName = tDynamic(room.name);
  const beforeThumb = isAfter ? beforeUrlFor(shot.key) : null;
  /** Photos of the items in this spot, for a spot the owner hasn't photographed yet. */
  const spotItemPhotos = (key: string) =>
    room.items
      .filter((i) => i.groupId === key && i.photoUrl)
      .map((i) => ({ url: i.photoUrl as string, name: i.name }));
  const shotCollage = shot.kind === "group" && !shot.refUrl ? spotItemPhotos(shot.key) : [];

  // ---------- room start (the whole-room shot) ----------
  const roomStart = (
    <>
      <div className="min-h-0 flex-1 overflow-y-auto bg-[#f4f7f9] px-4 pb-4 pt-4">
        <div className="flex flex-col gap-4">
          <OfflineLine />
          <div>
            <div className="text-[14px] font-bold text-[#15776d]">
              {fill(t("rwRoomOf"), { n: roomIndex + 1, total: rooms.length })}
            </div>
            <div className="text-[34px] font-bold leading-tight tracking-[-0.8px] text-[#0f1720]">{roomName}</div>
          </div>
          <Segments total={rooms.length} done={doneRooms} current={roomIndex} />
          <RefCarousel
            urls={room.noCover ? [] : room.refUrls.length ? room.refUrls : shot.refUrl ? [shot.refUrl] : []}
            label={t("bpReferencePhoto")}
            fallbackTitle={roomName}
            fallbackBody={t("rwWholeRoom")}
          />
          {spotCount === 0 && shot.itemNames.length > 0 ? (
            <div className="rounded-[22px] bg-white p-4">
              <div className="flex items-baseline gap-2">
                <div className="text-[18px] font-bold tracking-[-0.2px] text-[#0f1720]">{t("bpInThisRoom")}</div>
                <div className="text-[13px] font-medium text-[#8494a2]">
                  {fill(t("bpThings"), { n: shot.itemNames.length })}
                </div>
              </div>
              <div className="mt-3 flex flex-wrap gap-2">
                {(showAllNames ? shot.itemNames : shot.itemNames.slice(0, 12)).map((n, i) => (
                  <span key={`${n}-${i}`} className="rounded-full bg-[#f1f5f7] px-3.5 py-1.5 text-[14px] font-bold text-[#0f1720]">
                    {n}
                  </span>
                ))}
                {!showAllNames && shot.itemNames.length > 12 ? (
                  <button
                    type="button"
                    onClick={() => setShowAllNames(true)}
                    className="rounded-full bg-[#e2eeed] px-3.5 py-1.5 text-[14px] font-bold text-[#15776d]"
                  >
                    {fill(t("bpMore"), { n: shot.itemNames.length - 12 })}
                  </button>
                ) : null}
              </div>
            </div>
          ) : (
            <div className="flex gap-2.5">
              <span className="flex h-11 flex-1 items-center justify-center gap-2 rounded-[14px] bg-white text-[14.5px] font-bold text-[#475569]">
                <Camera className="h-4 w-4" />
                {t("bpOneRoom")}
              </span>
              <span className="flex h-11 flex-1 items-center justify-center gap-2 rounded-[14px] bg-white text-[14.5px] font-bold text-[#475569]">
                {spotCount === 1 ? t("bpSpotsOne") : fill(t("bpSpotsMany"), { n: spotCount })}
              </span>
            </div>
          )}
        </div>
      </div>
      {cta(
        <>
          <button type="button" onClick={() => openCamera({ kind: "shot", key: shot.key })} className={bigButton}>
            <Camera className="h-6 w-6" strokeWidth={2.2} />
            {t("gwTakePhoto")}
          </button>
          <button
            type="button"
            onClick={() => setSkipOpen(true)}
            className="flex h-10 items-center justify-center text-[14.5px] font-bold text-[#64748b]"
          >
            {t("rwCantGetIn")}
          </button>
        </>,
      )}
    </>
  );

  // ---------- a spot ----------
  const spotGuide = (
    <>
      <div className="min-h-0 flex-1 overflow-y-auto bg-[#f4f7f9] px-4 pb-4 pt-3">
        <div className="flex flex-col gap-3.5">
          <OfflineLine />
          <div className="flex items-center justify-between gap-3">
            <div className="min-w-0">
              <div className="truncate text-[12.5px] font-bold text-[#8494a2]">
                {roomName} · {t(isAfter ? "afterTitle" : "walkTitle")}
              </div>
              <div className="text-[20px] font-bold tracking-[-0.3px] text-[#0f1720]">
                {fill(t("bpSpotOf"), { n: shotIdx, m: spotCount })}
              </div>
            </div>
            <ProblemPill label={t("rwProblem")} onClick={openProblem} />
          </div>
          <Segments total={keys.length} done={takenCount} current={shotIdx} />
          <div className="rounded-[22px] bg-white p-3">
            {(() => {
              const corner = beforeThumb ? (
                <button
                  type="button"
                  onClick={() => setEnlarge({ url: beforeThumb, label: t("bpYourBefore") })}
                  className="flex flex-col items-end gap-1"
                >
                  <img src={beforeThumb} alt="" className="h-[64px] w-[84px] rounded-[10px] border-2 border-white object-cover shadow-[0_4px_14px_rgba(15,23,32,0.3)]" />
                  <span className="rounded-full bg-[rgba(13,20,27,0.78)] px-2 py-0.5 text-[11px] font-bold text-white">{t("bpYourBefore")}</span>
                </button>
              ) : null;
              return shotCollage.length > 0 ? (
                <ItemCollage photos={shotCollage} label={t("bpItemPhotos")} corner={corner} />
              ) : (
                <RefCarousel
                  urls={shot.refUrl ? [shot.refUrl] : []}
                  label={t("bpReferencePhoto")}
                  fallbackTitle={t("gwNoPictureTitle")}
                  fallbackBody={t("gwNoPictureBody")}
                  corner={corner}
                />
              );
            })()}
            <div className="px-1 pb-1 pt-3">
              <div className="text-[26px] font-bold leading-tight tracking-[-0.5px] text-[#0f1720]">{tDynamic(shot.title)}</div>
              <div className="mt-2.5">
                <ItemChips names={shot.itemNames} />
              </div>
            </div>
          </div>
        </div>
      </div>
      {cta(
        <button type="button" onClick={() => openCamera({ kind: "shot", key: shot.key })} className={bigButton}>
          <Camera className="h-6 w-6" strokeWidth={2.2} />
          {/^open\b/i.test(shot.prompt) ? t("bpOpenIt") : t("gwTakePhoto")}
        </button>,
      )}
    </>
  );

  // ---------- out in the open ----------
  const looseNames = room.shots[0]?.itemNames ?? [];
  const openScreen = (
    <>
      <div className="min-h-0 flex-1 overflow-y-auto bg-[#f4f7f9] px-4 pb-4 pt-3">
        <div className="flex flex-col gap-3.5">
          <div>
            <div className="text-[12.5px] font-bold text-[#8494a2]">
              {roomName} · {t(isAfter ? "afterTitle" : "walkTitle")}
            </div>
            <div className="text-[20px] font-bold tracking-[-0.3px] text-[#0f1720]">{t("bpAllSpotsDone")}</div>
          </div>
          <Segments total={keys.length} done={keys.length} />
          <div className="rounded-[22px] bg-white p-4">
            <div className="flex items-center gap-3">
              <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-[#e2eeed]">
                <Check className="h-5 w-5 text-[#15776d]" strokeWidth={3} />
              </span>
              <div>
                <div className="text-[19px] font-bold text-[#0f1720]">{t("bpOutInOpen")}</div>
                <div className="text-[13.5px] font-bold text-[#15776d]">{t("bpCoveredByRoom")}</div>
              </div>
            </div>
            <div className="mt-3.5">
              <ItemChips names={looseNames} max={12} />
            </div>
          </div>
        </div>
      </div>
      {cta(
        <button
          type="button"
          onClick={() => setOpenAck((p) => ({ ...p, [room.id]: true }))}
          className={bigButton}
        >
          <Check className="h-6 w-6" strokeWidth={2.6} />
          {fill(t("bpRoomDone"), { room: roomName })}
        </button>,
      )}
    </>
  );

  // ---------- room done ----------
  const completeScreen = (
    <>
      <div className="min-h-0 flex-1 overflow-y-auto bg-[#f4f7f9] px-4 pb-4 pt-6">
        <div className="flex flex-col items-center gap-1 text-center">
          <span className="flex h-16 w-16 items-center justify-center rounded-full bg-[#15776d]">
            <Check className="h-8 w-8 text-white" strokeWidth={3} />
          </span>
          <div className="mt-2 text-[28px] font-bold tracking-[-0.6px] text-[#0f1720]">
            {fill(t(isAfter ? "bpRoomPutAway" : "bpRoomDone"), { room: roomName })}
          </div>
          <div className="text-[14.5px] font-medium text-[#64748b]">
            {fill(t("bpRoomsDone"), { n: doneRooms + (statusOf(room.id) === "done" ? 0 : 1), m: rooms.length })}
          </div>
        </div>
        <div className="mt-5 grid grid-cols-2 gap-2.5 rounded-[22px] bg-white p-3">
          {room.shots.map((s) => {
            const g = taken[s.key];
            return (
              <button
                key={s.key}
                type="button"
                onClick={() => openCamera({ kind: "retake", key: s.key })}
                className="flex flex-col gap-1.5 text-left"
                aria-label={`${t("rwRetake")} ${s.title}`}
              >
                <div className="aspect-[4/3] w-full overflow-hidden rounded-[14px] bg-[#e6edf1]">
                  {g?.url ? <img src={g.url} alt="" className="h-full w-full object-cover" /> : null}
                </div>
                <div className="truncate text-[13.5px] font-bold text-[#0f1720]">{tDynamic(s.kind === "room" ? t("bpWholeRoom") : s.title)}</div>
              </button>
            );
          })}
        </div>
        <button
          type="button"
          onClick={() => openCamera({ kind: "extra" })}
          className="mt-3 flex h-12 w-full items-center justify-center rounded-[14px] border-[1.5px] border-[#d7e0e6] bg-white text-[15.5px] font-bold text-[#0f1720]"
        >
          {t("gwTakeAnother")}
          {extras.length > 0 ? ` · ${fill(t("gwSavedN"), { n: extras.length })}` : ""}
        </button>
      </div>
      {cta(
        <button type="button" disabled={busy} onClick={() => void goNextRoom()} className={bigButton}>
          {busy ? (
            <Loader2 className="h-6 w-6 animate-spin" />
          ) : nextRoom ? (
            <>
              {fill(t("gwNextRoom"), { room: tDynamic(nextRoom.name) })}
              <ChevronRight className="h-5 w-5" />
            </>
          ) : (
            t("gwCheckRooms")
          )}
        </button>,
      )}
    </>
  );

  // ---------- all rooms done ----------
  const skippedRooms = rooms.filter((r) => statusOf(r.id) === "skipped" && !(isAfter && beforeBy[r.id]?.skip));
  const handledRooms = rooms.filter((r) => statusOf(r.id) === "done");
  const reasonText = (r?: RoomSkipReason) => {
    const found = SKIP_REASONS.find((x) => x.id === r);
    return found ? t(found.key) : "";
  };
  const finishScreen = (
    <>
      <div className="min-h-0 flex-1 overflow-y-auto bg-[#f4f7f9] px-4 pb-4 pt-6">
        <div className="flex flex-col items-center gap-1 text-center">
          <span className="flex h-16 w-16 items-center justify-center rounded-full bg-[#15776d]">
            <Check className="h-8 w-8 text-white" strokeWidth={3} />
          </span>
          <div className="mt-2 text-[28px] font-bold tracking-[-0.6px] text-[#0f1720]">
            {t(isAfter ? "bpAfterDone" : "bpBeforeDone")}
          </div>
        </div>
        <div className="mt-5 flex flex-col overflow-hidden rounded-[20px] bg-white">
          <div className="flex items-center gap-3 px-4 py-3.5">
            <Check className="h-4 w-4 text-[#15776d]" strokeWidth={3} />
            <span className="text-[15.5px] font-bold text-[#0f1720]">{fill(t("bpRoomsCount"), { n: handledRooms.length })}</span>
          </div>
          {problemsTotal > 0 ? (
            <div className="flex items-center gap-3 border-t border-[#eef2f5] px-4 py-3.5">
              <AlertTriangle className="h-4 w-4 text-[#b45309]" strokeWidth={2.4} />
              <span className="text-[15.5px] font-bold text-[#0f1720]">
                {problemsTotal === 1 ? t("bpOneProblemSent") : fill(t("bpProblemsSent"), { n: problemsTotal })}
              </span>
            </div>
          ) : null}
        </div>
        {skippedRooms.map((r) => (
          <div key={r.id} className="mt-2.5 flex items-center gap-3 rounded-[16px] border-[1.5px] border-[#f1ddc2] bg-[#fdf3e8] p-3.5">
            <AlertTriangle className="h-6 w-6 shrink-0 text-[#b45309]" strokeWidth={2.2} />
            <div className="min-w-0 flex-1">
              <div className="text-[17px] font-bold text-[#0f1720]">{tDynamic(r.name)}</div>
              <div className="text-[13.5px] font-bold text-[#b45309]">
                {fill(t("rwSkippedReason"), { reason: reasonText(by[r.id]?.skip) })}
              </div>
            </div>
            <button
              type="button"
              onClick={() => void shootSkippedNow(r.id)}
              className="flex h-11 shrink-0 items-center rounded-xl bg-[#b45309] px-3.5 text-[14px] font-bold text-white"
            >
              {t("rwShootNow")}
            </button>
          </div>
        ))}
        <div className="mt-2.5 flex flex-col overflow-hidden rounded-[16px] bg-white">
          {handledRooms.map((r, i) => {
            const count = r.shots.filter((s) => by[r.id]?.shots?.[s.key]).length;
            const problems = problemsInRoom(r.name);
            return (
              <button
                key={r.id}
                type="button"
                onClick={() => openRoom(r.id)}
                className={cn("flex items-center gap-3 px-4 py-3.5 text-left", i > 0 && "border-t border-[#eef2f5]")}
              >
                <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-[#24a89a]">
                  <Check className="h-3.5 w-3.5 text-white" strokeWidth={3.2} />
                </span>
                <div className="min-w-0 flex-1">
                  <div className="text-[16px] font-bold text-[#0f1720]">{tDynamic(r.name)}</div>
                  {problems > 0 ? (
                    <div className="text-[12.5px] font-bold text-[#b4453a]">
                      {problems === 1 ? t("rwOneProblem") : fill(t("rwNProblems"), { n: problems })}
                    </div>
                  ) : null}
                </div>
                <div className="text-[14px] font-bold text-[#64748b]">{fill(t("gwNofMShots"), { n: count, m: r.shots.length })}</div>
                <ChevronRight className="h-[18px] w-[18px] text-[#c3ccd2]" />
              </button>
            );
          })}
        </div>
      </div>
      {cta(
        <button type="button" disabled={busy} onClick={() => void finish()} className={bigButton}>
          {busy ? <Loader2 className="h-6 w-6 animate-spin" /> : t(isAfter ? "bpContinue" : "bpContinueCleaning")}
        </button>,
      )}
    </>
  );

  // ---------- camera ----------
  const camShot = cam && (cam.kind === "shot" || cam.kind === "retake") ? room.shots.find((s) => s.key === cam.key) : null;
  const camKey = camShot?.key ?? shot.key;
  const camIdx = Math.max(0, keys.indexOf(camKey));
  const camTitle = cam?.kind === "extra" ? t("gwExtraTitle") : cam?.kind === "problem" ? t("rwProblem") : camShot?.kind === "room" || !camShot ? roomName : tDynamic(camShot.title);
  const camSub =
    cam?.kind === "problem"
      ? t("takePhotoOfProblem")
      : cam?.kind === "extra"
        ? roomName
        : camShot?.kind === "room"
          ? t("bpWholeRoom")
          : fill(t("bpSpotOf"), { n: camIdx, m: spotCount });
  const camBefore = isAfter && cam && cam.kind !== "problem" && cam.kind !== "extra" ? beforeUrlFor(camKey) : null;
  const camRef =
    cam?.kind === "retake" && !isAfter
      ? taken[cam.key]?.url ?? null
      : !isAfter && camShot
        ? camShot.refUrl
        : null;
  const camCollage =
    !isAfter && camShot && camShot.kind === "group" && !camShot.refUrl && cam?.kind === "shot"
      ? spotItemPhotos(camShot.key)
      : [];
  const camera = (
    <div className="fixed inset-0 z-[60] flex flex-col bg-[#0d141b]">
      <video ref={videoRef} playsInline muted autoPlay className={cn("absolute inset-0 h-full w-full object-cover", !cameraReady && "opacity-0")} />
      {!cameraReady ? (
        <div className="absolute inset-0 z-[1] flex flex-col items-center justify-center gap-3 px-6 text-center">
          {busy ? null : (
            <>
              <div className="text-[17px] font-bold text-white/80">{cameraError || t("startingCamera")}</div>
              <button type="button" onClick={() => void startCamera()} className="rounded-full bg-white/15 px-5 py-2.5 text-[15px] font-bold text-white">
                {t("openCamera")}
              </button>
              <div className="text-[13px] font-medium text-white/60">{t("photosInAppOnly")}</div>
            </>
          )}
        </div>
      ) : null}
      {flash ? <div className="pointer-events-none absolute inset-0 z-[3] bg-white/80" style={{ animation: "walkFlash 180ms ease-out forwards" }} /> : null}

      <div className="relative z-[2] flex flex-col gap-3 bg-gradient-to-b from-[rgba(13,20,27,0.85)] to-transparent px-4 pb-6 pt-[max(1rem,env(safe-area-inset-top))]">
        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={closeCamera}
            aria-label={t("gwCancel")}
            className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-white/15 text-[20px] font-bold text-white"
          >
            ×
          </button>
          <div className="min-w-0 flex-1">
            <div className="truncate text-[18px] font-bold leading-tight text-white">{camTitle}</div>
            <div className="truncate text-[13px] font-medium text-white/70">
              {cam?.kind === "retake" ? t("gwRetakeTitle") : camSub}
            </div>
          </div>
          {cam?.kind !== "problem" ? <ProblemPill dark label={t("rwProblem")} onClick={openProblem} /> : null}
        </div>
        <OfflineLine className="self-stretch" />
        <div className="flex justify-end">
          {camBefore ? (
            <CornerThumb url={camBefore} label={t("bpYourBefore")} onClick={() => setEnlarge({ url: camBefore, label: t("bpYourBefore") })} />
          ) : camCollage.length > 0 ? (
            <CollageThumb photos={camCollage} label={t("bpItemPhotos")} />
          ) : camRef ? (
            <CornerThumb
              url={camRef}
              label={cam?.kind === "retake" ? t("gwYourFirst") : t("bpReference")}
              onClick={() => setEnlarge({ url: camRef, label: cam?.kind === "retake" ? t("gwYourFirst") : t("bpReference") })}
            />
          ) : null}
        </div>
      </div>

      <div className="flex-1" />

      <div className="relative z-[2] flex flex-col items-center gap-2 bg-gradient-to-t from-[rgba(13,20,27,0.9)] to-transparent px-5 pb-[max(1.5rem,env(safe-area-inset-bottom))] pt-10">
        <button
          type="button"
          disabled={busy}
          onClick={() => void shutter()}
          aria-label={t("capture")}
          className="flex h-[84px] w-[84px] shrink-0 items-center justify-center rounded-full border-[5px] border-white/30 bg-white active:scale-95 disabled:opacity-70"
        >
          {busy ? <Loader2 className="h-7 w-7 animate-spin text-[#24a89a]" /> : null}
        </button>
      </div>
    </div>
  );

  // ---------- keep or retake ----------
  const prevKey = preview && preview.mode.kind !== "extra" ? preview.mode.key : null;
  const pvBefore = isAfter && prevKey ? beforeUrlFor(prevKey) : null;
  const pvIsWhole = preview?.mode.kind === "shot" && preview.mode.key === "room";
  const previewScreen = preview ? (
    <div className="fixed inset-0 z-[60] flex flex-col bg-[#0d141b]">
      <img src={swap && pvBefore ? pvBefore : preview.localUrl} alt="" className="absolute inset-0 h-full w-full object-cover" />
      <div className="relative z-[2] flex items-start gap-3 bg-gradient-to-b from-[rgba(13,20,27,0.85)] to-transparent px-4 pb-6 pt-[max(1rem,env(safe-area-inset-top))]">
        <div className="min-w-0 flex-1">
          <div className="truncate text-[18px] font-bold leading-tight text-white">
            {preview.mode.kind === "extra"
              ? t("gwExtraTitle")
              : preview.mode.key === "room"
                ? roomName
                : tDynamic(room.shots.find((s) => s.key === (preview.mode as { key: string }).key)?.title ?? roomName)}
          </div>
          {isAfter && pvBefore ? (
            <span className="mt-1.5 inline-block rounded-full bg-[#15776d] px-3 py-1 text-[12.5px] font-bold text-white">
              {swap ? t("bpBeforeChip") : t("bpAfterChip")}
            </span>
          ) : null}
        </div>
        <ProblemPill dark label={t("rwProblem")} onClick={openProblem} />
      </div>
      {pvBefore ? (
        <div className="relative z-[2] flex justify-end px-4">
          <CornerThumb
            url={swap ? preview.localUrl : pvBefore}
            label={swap ? t("bpYourAfter") : t("bpCompare")}
            onClick={() => setSwap((v) => !v)}
          />
        </div>
      ) : null}
      <div className="flex-1" />
      <div className="relative z-[2] flex flex-col gap-2.5 rounded-t-[26px] bg-white px-4 pb-[max(1.25rem,env(safe-area-inset-bottom))] pt-4">
        <div className="flex gap-2.5">
          <button
            type="button"
            disabled={busy}
            onClick={() => {
              const mode = preview.mode;
              dropPreview();
              openCamera(mode);
            }}
            className="flex h-[60px] flex-1 items-center justify-center gap-2 rounded-[16px] bg-[#e2eeed] text-[17px] font-bold text-[#15776d] disabled:opacity-60"
          >
            <RotateCcw className="h-5 w-5" strokeWidth={2.4} />
            {t("rwRetake")}
          </button>
          <button
            type="button"
            disabled={busy}
            onClick={() => void keep(false)}
            className="flex h-[60px] flex-[1.5] items-center justify-center gap-2 rounded-[16px] bg-[#15776d] text-[18px] font-bold text-white disabled:opacity-80"
          >
            {busy ? (
              <>
                <Loader2 className="h-5 w-5 animate-spin" />
                {t("bpSaving")}
              </>
            ) : (
              <>
                <Check className="h-5 w-5" strokeWidth={3} />
                {t("bpKeep")}
              </>
            )}
          </button>
        </div>
        {pvIsWhole || preview.mode.kind === "extra" ? (
          <button
            type="button"
            disabled={busy}
            onClick={() => void keep(true)}
            className="flex h-12 items-center justify-center rounded-[14px] bg-[#f4f7f9] text-[15.5px] font-bold text-[#475569] disabled:opacity-60"
          >
            + {t("gwTakeAnother")}
          </button>
        ) : null}
      </div>
    </div>
  ) : null;

  const showOpen = screen === "guide" && roomComplete && !openAck[room.id] && looseNames.length > 0 && statusOf(room.id) !== "done";

  return (
    <div className={cn(CLEANER_SIMPLE_STEP_SHELL, "relative text-[#0f1720]")}>
      {screen !== "camera" && screen !== "preview" ? header : null}
      {screen === "guide" && !roomComplete ? (shot.kind === "room" ? roomStart : spotGuide) : null}
      {showOpen ? openScreen : null}
      {screen === "guide" && roomComplete && !showOpen ? completeScreen : null}
      {screen === "finish" ? finishScreen : null}
      {screen === "camera" ? camera : null}
      {screen === "preview" ? previewScreen : null}

      {enlarge ? <PhotoOverlay url={enlarge.url} label={enlarge.label} closeLabel={t("close")} onClose={() => setEnlarge(null)} /> : null}

      {skipOpen ? (
        <div className="fixed inset-0 z-[80] flex flex-col justify-end">
          <button type="button" className="absolute inset-0 bg-[rgba(15,23,32,0.55)]" aria-label={t("close")} onClick={() => !busy && setSkipOpen(false)} />
          <div className="relative z-10 flex flex-col gap-4 rounded-t-[26px] bg-white px-[18px] pb-[max(1.5rem,env(safe-area-inset-bottom))] pt-5">
            <div className="mx-auto h-1 w-11 rounded-full bg-[#dde5eb]" />
            <div className="text-[22px] font-bold tracking-[-0.4px]">{fill(t("rwWhySkip"), { room: roomName })}</div>
            <div className="flex flex-col gap-2.5">
              {SKIP_REASONS.map((r) => (
                <button
                  key={r.id}
                  type="button"
                  onClick={() => setSkipReason(r.id)}
                  className={cn(
                    "flex h-[56px] items-center rounded-[14px] border-[1.5px] px-4 text-[17px] font-bold",
                    skipReason === r.id ? "border-[#0f1720] bg-[#f1f5f7] text-[#0f1720]" : "border-[#e6edf1] bg-white text-[#475569]",
                  )}
                >
                  {t(r.key)}
                </button>
              ))}
            </div>
            <div className="text-[14px] font-medium text-[#64748b]">{t("rwSkipNote")}</div>
            <button
              type="button"
              disabled={!skipReason || busy}
              onClick={() => void skipThisRoom()}
              className={cn(
                "flex h-14 items-center justify-center rounded-[15px] text-[17.5px] font-bold",
                skipReason ? "bg-[#0f1720] text-white" : "bg-[#cfd9de] text-white",
              )}
            >
              {busy ? <Loader2 className="h-5 w-5 animate-spin" /> : t("rwSkipRoom")}
            </button>
            <button type="button" onClick={() => setSkipOpen(false)} className="h-11 text-[16px] font-bold text-[#64748b]">
              {t("rwGoBack")}
            </button>
          </div>
        </div>
      ) : null}

      {issueOpen ? (
        <div className="fixed inset-0 z-[80] flex flex-col justify-end">
          <button type="button" className="absolute inset-0 bg-[rgba(15,23,32,0.55)]" aria-label={t("close")} onClick={() => !busy && setIssueOpen(false)} />
          <div className="relative z-10 flex max-h-[92dvh] w-full flex-col gap-4 overflow-y-auto rounded-t-[26px] bg-white px-[18px] pb-[max(1.5rem,env(safe-area-inset-bottom))] pt-5">
            <div className="mx-auto h-1 w-11 shrink-0 rounded-full bg-[#dde5eb]" />
            {issueSent ? (
              <>
                <div className="flex flex-col items-center gap-2 py-4 text-center">
                  <span className="flex h-14 w-14 items-center justify-center rounded-full bg-[#15776d]">
                    <Check className="h-7 w-7 text-white" strokeWidth={3} />
                  </span>
                  <div className="text-[24px] font-bold tracking-[-0.4px]">{t("bpSent")}</div>
                  <div className="text-[15.5px] font-medium text-[#64748b]">{issueSent}</div>
                </div>
                <button type="button" onClick={() => setIssueOpen(false)} className={bigButton}>
                  {t("bpBackToPhoto")}
                </button>
              </>
            ) : (
              <>
                <div className="flex items-center justify-between gap-3">
                  <div>
                    <div className="text-[24px] font-bold tracking-[-0.4px]">{t("rwProblem")}</div>
                    <div className="text-[14px] font-medium text-[#8494a2]">{shot.kind === "room" ? roomName : tDynamic(shot.title)}</div>
                  </div>
                  <button
                    type="button"
                    onClick={() => setIssueOpen(false)}
                    aria-label={t("close")}
                    className="flex h-11 w-11 items-center justify-center rounded-full bg-[#f1f5f7] text-[20px] font-bold text-[#475569]"
                  >
                    ×
                  </button>
                </div>
                <div className="flex flex-wrap gap-2">
                  {itemsForShot.map((it) => (
                    <button
                      key={it.id}
                      type="button"
                      onClick={() => setIssueItemId(it.id)}
                      className={cn(
                        "flex min-h-[48px] items-center rounded-[14px] px-4 text-[15.5px] font-bold",
                        issueItemId === it.id ? "bg-[#15776d] text-white" : "bg-[#f4f7f9] text-[#0f1720]",
                      )}
                    >
                      {it.name}
                    </button>
                  ))}
                  <button
                    type="button"
                    onClick={() => setIssueItemId(null)}
                    className={cn(
                      "flex min-h-[48px] items-center rounded-[14px] px-4 text-[15.5px] font-bold",
                      issueItemId === null ? "bg-[#15776d] text-white" : "bg-[#f4f7f9] text-[#64748b]",
                    )}
                  >
                    {t("gwSomethingElse")}
                  </button>
                </div>
                <div className="grid grid-cols-3 gap-2.5">
                  {CATEGORIES.map((c) => (
                    <button
                      key={c.id}
                      type="button"
                      onClick={() => setIssueCat(c.id)}
                      className={cn(
                        "flex h-[60px] items-center justify-center rounded-[16px] text-[16px] font-bold",
                        issueCat === c.id ? "bg-[#15776d] text-white" : "bg-[#f4f7f9] text-[#0f1720]",
                      )}
                    >
                      {t(c.key)}
                    </button>
                  ))}
                </div>
                <button
                  type="button"
                  onClick={() => {
                    setIssueOpen(false);
                    openCamera({ kind: "problem" });
                  }}
                  className="flex h-[56px] items-center justify-center gap-2.5 rounded-[16px] bg-[#e2eeed] text-[16.5px] font-bold text-[#15776d]"
                >
                  {issuePhotoUrl ? (
                    <>
                      <img src={issuePhotoUrl} alt="" className="h-9 w-9 rounded-[8px] object-cover" />
                      {t("rwRetake")}
                    </>
                  ) : (
                    <>
                      <Camera className="h-5 w-5" />
                      {t("bpAddPhoto")}
                    </>
                  )}
                </button>
                <button
                  type="button"
                  disabled={busy || issueItemId === undefined || !issueCat}
                  onClick={() => void sendIssue()}
                  className={cn(
                    "flex h-14 w-full shrink-0 items-center justify-center rounded-[15px] text-[17.5px] font-bold",
                    issueItemId !== undefined && issueCat ? "bg-[#15776d] text-white" : "bg-[#e6ecf0] text-[#8494a2]",
                  )}
                >
                  {busy ? <Loader2 className="h-5 w-5 animate-spin" /> : t("bpSend")}
                </button>
              </>
            )}
          </div>
        </div>
      ) : null}

      <style>{`@keyframes walkFlash { from { opacity: 0.85; } to { opacity: 0; } }`}</style>
    </div>
  );
}
